import React from "react";
import type { AppProps } from "next/app";
import {
  FirebaseAppProvider,
  preloadFirestore,
  useFirebaseApp,
} from "reactfire";
import { config as firebaseConfig } from "~/lib/firebase";
import Auth from "~/components/auth/Auth";
import { ThemeProvider, Theme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import GlobalStyles from "@mui/material/GlobalStyles";
import theme from "../theme";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ClientSideOnly from "../components/ClientSideOnly";
import Header from "~/components/Header";
import { Box } from "@mui/material";
import Footer from "~/components/Footer";
import { CacheProvider, EmotionCache } from "@emotion/react";
import { createEmotionCache } from "~/lib/browser-util";
import GlobalAlerts from "~/components/GlobalAlerts";
import { localStorageProvider } from "~/lib/swr";
import { SWRConfig } from "swr";

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache;
}

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

const globalStyles = (
  <GlobalStyles
    styles={(theme) => ({
      a: { color: theme.palette.primary.main },
    })}
  />
);

export default function MyApp(props: MyAppProps): React.ReactElement {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;

  // elements rendered here will persist across client-side navigation events
  return (
    <CacheProvider value={emotionCache}>
      <FirebaseAppProvider firebaseConfig={firebaseConfig}>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <SWRConfig value={{ provider: localStorageProvider }}>
              <CssBaseline enableColorScheme />
              {globalStyles}
              <Auth />
              <Header />
              <GlobalAlerts />
              <Box id="component-wrapper">
                <Component {...pageProps} />
              </Box>
              <ClientSideOnly />
              <Box>
                <Footer />
              </Box>
            </SWRConfig>
          </LocalizationProvider>
        </ThemeProvider>
      </FirebaseAppProvider>
    </CacheProvider>
  );
}
